import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import PageActions, {
    VIEWPORT_CLASS_LARGE,
} from "../../../../utils/page/library";
import LandingNav from "../LandingNav/component";
import TertiaryNav from "../TertiaryNav/component";
import ExternalNav from "./components/ExternalNav/component";
import IconLogo from "../../../Icon/IconLogo";
import IconSearch from "../../../Icon/IconSearch";
import Logo from "../../../Logo";
import LinkWrap from "../../../LinkWrap/component";
import { getTertiaryNavList } from "../SecondaryNav/library";
import {
    normalizeClass,
    makeAbsoluteCBCDomain,
    getBaseSection,
    getSectionByIndex,
} from "../../../../utils/path/library";
import classNames from "classnames";
import ExternalProfileButton from "../ExternalProfileButton/component";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { FEATURE_FLAG } from "../ExternalProfileButton/constants";

/**
 * Function representing the ExternalHeader component.
 *
 * This is the externally included global header, usually imported via iFrames
 */
const ExternalHeader = ({ path = "" }) => {
    const [viewport, setViewport] = useState(VIEWPORT_CLASS_LARGE);
    const domNode = useRef(null);
    const showExternalSignIn = useFeatureFlag(FEATURE_FLAG);

    useEffect(() => {
        const componentInfo = {
            domNode: domNode,
            callback: _viewportTransformCallback,
        };
        PageActions.registerMediaQueryObject(componentInfo);
    }, []);

    const renderLogoExternal = () => {
        const logoProps = {
            className: "headerLogo globalHeaderLogo",
        };

        const logoLinkProps = {
            className: "logo",
            external: true,
            url: makeAbsoluteCBCDomain("/"),
        };

        return (
            <LinkWrap {...logoLinkProps} aria-label="CBC Home Page">
                <IconLogo {...logoProps} />
            </LinkWrap>
        );
    };

    const renderSearchButtonExternal = () => {
        const searchIconProps = {
            "aria-expanded": "false",
            "aria-label": "Search",
            className: "navLink search",
            external: true,
            url: makeAbsoluteCBCDomain("/search"),
            id: "searchButton",
            role: "button",
        };

        return (
            <LinkWrap {...searchIconProps}>
                <IconSearch className="headerSearch" />
            </LinkWrap>
        );
    };

    /**
     * Allows updating the Header state from PageActions depending on media queries.
     * @param {string} mediaQueryClass The class name to be added to the header
     * @returns {void}
     */
    const _viewportTransformCallback = (mediaQueryClass) => {
        setViewport(mediaQueryClass);
    };

    /**
     * This function strips everything from the path before the base section + preceding subpaths
     * @returns {string} the relevant path info for sub navs
     */
    const getStrippedRoutePath = () => {
        const [, , , ...routeParams] = path.split("/");
        return routeParams.join("/");
    };

    let landingNav = null;
    let tertiaryNav = null;
    const routePath = getStrippedRoutePath();
    const landingSection = routePath ? getBaseSection(routePath) : "";

    // If the external header was passed the section in the path (e.g. news, books, arts) show its Landing navigation
    // Possibly there's a third-level navigation to be displayed too (e.g. /sports/hockey/nhl/teams)
    if (landingSection) {
        const tertiaryNavList = getTertiaryNavList(routePath);

        if (tertiaryNavList) {
            // If the section that was passed indicates there's a third level of navigation, show TertiryNav below LandingNav
            const tertiaryNavProps = {
                items: tertiaryNavList,
                baseNavSection: landingSection,
                subSection: getSectionByIndex(routePath, 1),
                path: routePath,
                isExternalHeader: true,
                viewport: viewport,
            };
            tertiaryNav = <TertiaryNav {...tertiaryNavProps} />;
        }

        const iconProps = {
            className: classNames("subSectionLogo", landingSection),
        };

        const logo = iconProps.section ? (
            <Logo {...iconProps} />
        ) : (
            <IconLogo {...iconProps} />
        );

        // Get the Logo of the brand name
        const gemDepartment = (
            <div className="gemSectionLink" id="section-link-title">
                <LinkWrap
                    className="navLink subNavLink"
                    url={makeAbsoluteCBCDomain(landingSection)}
                    external={true}
                >
                    {logo}
                </LinkWrap>
            </div>
        );

        const landingProps = {
            baseNavSection: landingSection,
            path,
            gemDepartment: gemDepartment,
            isExternalHeader: true,
            viewport: viewport,
            hasTertiaryNav: tertiaryNav !== null,
        };

        // Show the secondary Landing navigation
        landingNav = <LandingNav {...landingProps} />;
    }

    const containerClasses = classNames(
        "appContainer",
        normalizeClass("", landingSection, "Theme", false),
        viewport
    );

    return (
        <div className={containerClasses} ref={domNode}>
            <header
                className="globalHeader externalHeader"
                data-testid="external-header"
            >
                <div className="menuNavWrapper">
                    <base target="_blank" />
                    {renderLogoExternal()}
                    <ExternalNav path={path} />
                    <div className="sideMenu">
                        {renderSearchButtonExternal()}
                        {showExternalSignIn && (
                            <ExternalProfileButton path={path} />
                        )}
                    </div>
                </div>
                {landingNav}
                {tertiaryNav}
            </header>
        </div>
    );
};

ExternalHeader.propTypes = {
    path: PropTypes.string,
};

export default ExternalHeader;
